import React from "react";
import { Controller } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";

export const CustomizedDatePicker = ({
  name,
  control,
  label,
  required = false,
  format = "DD-MM-YYYY",
  disableFuture = false,
  disablePast = false,
  requiredMessage,
  placeholder,
  selectedWeekday,
  selectedDayOfMonth,
  minDate,
  startDate,
  rules = {},
  handleDisabledFutureDate = false,
  ...props
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DemoContainer components={["DatePicker"]} style={{ width: "100%" }}>
        <Controller
          name={name}
          control={control}
          rules={{
            ...(required ? { required: requiredMessage } : {}),
            validate: {
              validDate: (value) => {
                if (!value) return true;
                if (!dayjs(value).isValid()) return "Invalid date format";

                if (startDate) {
                  const start = dayjs(startDate);
                  const end = dayjs(value);

                  if (end.isBefore(start, "day")) {
                    return "End date cannot be before start date";
                  }

                  if (end.diff(start, "day") >= 30) {
                    return "End date cannot be more than 30 days after start date";
                  }
                }

                if (selectedWeekday) {
                  const dayName = dayjs(value).format("dddd");
                  if (dayName !== selectedWeekday)
                    return `Select a ${selectedWeekday}`;
                }

                if (selectedDayOfMonth) {
                  const dateNum = dayjs(value).date();
                  if (dateNum !== Number(selectedDayOfMonth))
                    return `Select date ${selectedDayOfMonth} of the month`;
                }

                if (disablePast) {
                  let minDate = dayjs(disablePast);
                  const today = dayjs();
                  if (today.isAfter(minDate, "day")) {
                    minDate = today;
                  }

                  if (dayjs(value).isBefore(minDate, "day")) {
                    return `Date cannot be before ${minDate.format(
                      "DD-MM-YYYY"
                    )}`;
                  }
                }

                if (
                  handleDisabledFutureDate &&
                  dayjs(value).isAfter(dayjs(), "day")
                ) {
                  return "Date cannot be a future date";
                }

                if (minDate) {
                  const min = dayjs(minDate);
                  if (dayjs(value).isBefore(min, "day")) {
                    return `Date cannot be before ${min.format("DD-MM-YYYY")}`;
                  }
                }

                return true;
              },
            },

            ...rules,
          }}
          render={({ field, fieldState }) => (
            <DatePicker
              {...field}
              value={field.value || null}
              onChange={(newValue) => field.onChange(newValue)}
              format={format}
              disableFuture={disableFuture}
              disablePast={disablePast}
              shouldDisableDate={(date) => {
                if (startDate) {
                  const start = dayjs(startDate);
                  if (dayjs(date).isBefore(start, "day")) return true;
                  if (dayjs(date).diff(start, "day") >= 30) return true;
                }
                if (selectedWeekday) {
                  return dayjs(date).format("dddd") !== selectedWeekday;
                }
                if (selectedDayOfMonth) {
                  return dayjs(date).date() !== Number(selectedDayOfMonth);
                }
                if (disablePast) {
                  const minDate = dayjs.isDayjs(disablePast)
                    ? disablePast
                    : dayjs(disablePast === true ? dayjs() : disablePast);
                  if (dayjs(date).isBefore(minDate, "day")) return true;
                }

                if (minDate) {
                  if (dayjs(date).isBefore(dayjs(minDate), "day")) return true;
                }

                return false;
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  label,
                  placeholder: placeholder,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
              sx={{ width: "100%" }}
              {...props}
            />
          )}
        />
      </DemoContainer>
    </LocalizationProvider>
  );
};
